import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ComputerIcon from '@mui/icons-material/Computer';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import FolderIcon from '@mui/icons-material/Folder';
import GetAppIcon from '@mui/icons-material/GetApp';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import ShieldIcon from '@mui/icons-material/Shield';
import TuneIcon from '@mui/icons-material/Tune';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button, Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import { SECURITY_PAGE_FILTER_KEYS } from 'src/mindtime/constants/securityFilters';
import { useSecurityPageFilters } from 'src/mindtime/hooks/useSecurityPageFilters';
import { getSecurityNavItemPath, SECURITY_ENDPOINTS_HREF } from 'src/mindtime/config/securityNav';
import logger from 'src/utils/logger';
import {
  EDR_COLORS,
  EdrDataCard,
  EdrDataToolbar,
  EdrDateTime,
  EdrKpiCard,
  EdrOverflowMenu,
  EdrPageHeader,
  EdrStatusPill,
  EdrTableFooter,
  edrMonoStyle,
  edrMutedStyle,
} from '../shared/edrPrimitives';
import type { Column, TableData } from 'src/types/table';

type ResponseStatus = 'Success' | 'Failed';

type AutomatedActionKey =
  | 'isolate-endpoint'
  | 'kill-process'
  | 'block-source-ip'
  | 'disable-user'
  | 'quarantine-file';

type ResponseRow = {
  id: string;
  timestamp: string;
  trigger: string;
  action: AutomatedActionKey;
  endpoint: string;
  organization: string;
  status: ResponseStatus;
  durationSec: number;
};

const TEAL_ACCENT = '#0D9488';

const MOCK_ROWS: ResponseRow[] = [
  {
    id: '1',
    timestamp: 'Today 12:45',
    trigger: 'Ransomware Detected',
    action: 'isolate-endpoint',
    endpoint: 'FIN-SRV-02',
    organization: 'Acme Financial',
    status: 'Success',
    durationSec: 4,
  },
  {
    id: '2',
    timestamp: 'Today 11:22',
    trigger: 'Malware Detected',
    action: 'kill-process',
    endpoint: 'HR-DEV-01',
    organization: 'TechCorp Ltd',
    status: 'Success',
    durationSec: 2,
  },
  {
    id: '3',
    timestamp: 'Today 09:15',
    trigger: 'Multiple Failed Logins',
    action: 'block-source-ip',
    endpoint: 'AUTH-SRV-03',
    organization: 'Global Retail',
    status: 'Success',
    durationSec: 6,
  },
  {
    id: '4',
    timestamp: 'Today 08:30',
    trigger: 'Malware Detected',
    action: 'quarantine-file',
    endpoint: 'MAIL-SRV-02',
    organization: 'HealthFirst',
    status: 'Failed',
    durationSec: 7,
  },
  {
    id: '5',
    timestamp: 'Yesterday',
    trigger: 'Unauthorized File Modification',
    action: 'quarantine-file',
    endpoint: 'WEB-APP-07',
    organization: 'Acme Financial',
    status: 'Failed',
    durationSec: 8,
  },
  {
    id: '6',
    timestamp: 'Yesterday',
    trigger: 'Suspicious PowerShell',
    action: 'kill-process',
    endpoint: 'OPS-WS-12',
    organization: 'TechCorp Ltd',
    status: 'Success',
    durationSec: 3,
  },
  {
    id: '7',
    timestamp: '2 Days Ago',
    trigger: 'Privilege Escalation Attempt',
    action: 'disable-user',
    endpoint: 'DB-SRV-01',
    organization: 'HealthFirst',
    status: 'Success',
    durationSec: 5,
  },
  {
    id: '8',
    timestamp: '2 Days Ago',
    trigger: 'Ransomware Detected',
    action: 'isolate-endpoint',
    endpoint: 'FIN-SRV-04',
    organization: 'Global Retail',
    status: 'Success',
    durationSec: 4,
  },
];

const ACTION_META: Record<AutomatedActionKey, { label: string; icon: ReactNode; color: string }> = {
  'isolate-endpoint': {
    label: 'Isolate Endpoint',
    icon: <ShieldIcon style={{ fontSize: 14 }} />,
    color: EDR_COLORS.critical,
  },
  'kill-process': {
    label: 'Kill Process',
    icon: <HighlightOffIcon style={{ fontSize: 14 }} />,
    color: EDR_COLORS.high,
  },
  'block-source-ip': {
    label: 'Block Source IP',
    icon: <BlockIcon style={{ fontSize: 14 }} />,
    color: EDR_COLORS.brand500,
  },
  'disable-user': {
    label: 'Disable User Account',
    icon: <PersonOffIcon style={{ fontSize: 14 }} />,
    color: EDR_COLORS.medium,
  },
  'quarantine-file': {
    label: 'Quarantine File',
    icon: <FolderIcon style={{ fontSize: 14 }} />,
    color: EDR_COLORS.slate500,
  },
};

const ActionTag: FC<{ value: AutomatedActionKey }> = ({ value }) => {
  const meta = ACTION_META[value];

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        color: EDR_COLORS.textBody,
      }}
    >
      <span style={{ color: meta.color, display: 'inline-flex' }}>{meta.icon}</span>
      {meta.label}
    </span>
  );
};

const breadcrumb = {
  links: [
    { name: 'EDR', to: SECURITY_ENDPOINTS_HREF },
    { name: 'Automated Response', to: getSecurityNavItemPath('automated-response') ?? '' },
  ],
};

const PAGE_SIZE = 8;
const ACTIVE_RULES_COUNT = 28;

const handleViewDetails = (row: ResponseRow) => {
  logger(`View automated response details: ${row.id}`, 'info', 'AutomatedResponse');
};

const AutomatedResponse: FC = () => {
  const { search, setSearchInput, resetFilters } = useSecurityPageFilters(
    SECURITY_PAGE_FILTER_KEYS.automatedResponse,
    { debounceMs: false }
  );
  const [page, setPage] = useState(0);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return MOCK_ROWS;

    return MOCK_ROWS.filter((row) => {
      const actionLabel = ACTION_META[row.action].label.toLowerCase();
      return (
        row.trigger.toLowerCase().includes(query)
        || actionLabel.includes(query)
        || row.endpoint.toLowerCase().includes(query)
        || row.organization.toLowerCase().includes(query)
        || row.status.toLowerCase().includes(query)
      );
    });
  }, [search]);

  const pageRows = useMemo(() => {
    const start = page * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, page]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));

  const responsesToday = MOCK_ROWS.filter((row) => row.timestamp.startsWith('Today')).length;
  const successfulCount = MOCK_ROWS.filter((row) => row.status === 'Success').length;
  const failedCount = MOCK_ROWS.filter((row) => row.status === 'Failed').length;
  const successRate = MOCK_ROWS.length
    ? Math.round((successfulCount / MOCK_ROWS.length) * 100)
    : 0;

  const columns: Column[] = useMemo(
    () => [
      {
        id: 'timestamp',
        label: 'Timestamp',
        render: (row) => (
          <EdrDateTime value={(row as unknown as ResponseRow).timestamp} />
        ),
      },
      {
        id: 'trigger',
        label: 'Trigger Event',
        render: (row) => (
          <span style={{ fontWeight: 600, color: EDR_COLORS.textPrimary }}>
            {(row as unknown as ResponseRow).trigger}
          </span>
        ),
      },
      {
        id: 'action',
        label: 'Automated Action',
        render: (row) => <ActionTag value={(row as unknown as ResponseRow).action} />,
      },
      {
        id: 'endpoint',
        label: 'Endpoint',
        render: (row) => (
          <span style={edrMonoStyle}>
            {(row as unknown as ResponseRow).endpoint}
          </span>
        ),
      },
      {
        id: 'organization',
        label: 'Organization',
        render: (row) => (
          <span style={edrMutedStyle}>
            {(row as unknown as ResponseRow).organization}
          </span>
        ),
      },
      {
        id: 'status',
        label: 'Status',
        render: (row) => {
          const item = row as unknown as ResponseRow;
          return (
            <EdrStatusPill
              tone={item.status === 'Success' ? 'success' : 'critical'}
              label={item.status}
            />
          );
        },
      },
      {
        id: 'duration',
        label: 'Duration',
        render: (row) => (
          <span style={{ color: EDR_COLORS.textSecondary }}>
            {(row as unknown as ResponseRow).durationSec}
            {' '}
            sec
          </span>
        ),
      },
      {
        id: 'actions',
        label: '',
        align: 'right',
        render: (row) => (
          <EdrOverflowMenu
            items={[
              { label: 'View Details' },
              { label: 'View Endpoint' },
              { label: 'Rerun Action' },
              { label: 'Disable Rule', tone: 'danger' },
            ]}
            onView={() => handleViewDetails(row as unknown as ResponseRow)}
          />
        ),
      },
    ],
    []
  );

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Security — Automated Response')}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{ gap: 16, minHeight: '100%', boxSizing: 'border-box' }}
      >
        <EdrPageHeader
          breadcrumb={breadcrumb}
          title="Automated Response"
          subtitle="Review and monitor automated response actions executed across your environment."
          actions={(
            <>
              <Button variant="outlined" startIcon={<GetAppIcon />} style={{ textTransform: 'none' }}>
                Export
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                style={{ textTransform: 'none' }}
              >
                Create Response Rule
              </Button>
            </>
          )}
        />

        <Box display="grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16 }}>
          <EdrKpiCard
            label="Automated Responses Today"
            value={responsesToday.toLocaleString()}
            icon={<FlashOnIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.brand500}
            accent={EDR_COLORS.brand500}
          >
            <EdrStatusPill tone="brand" label="Executed in the last 24 hours" dot={false} />
          </EdrKpiCard>
          <EdrKpiCard
            label="Successful Responses"
            value={successfulCount.toLocaleString()}
            icon={<CheckCircleOutlineIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.success}
            accent={EDR_COLORS.success}
          >
            <EdrStatusPill tone="success" label={`${successRate}% success rate`} dot={false} />
          </EdrKpiCard>
          <EdrKpiCard
            label="Failed Responses"
            value={failedCount.toLocaleString()}
            icon={<HighlightOffIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.critical}
            accent={EDR_COLORS.critical}
          >
            <EdrStatusPill tone="critical" label="Require manual follow-up" dot={false} />
          </EdrKpiCard>
          <EdrKpiCard
            label="Active Response Rules"
            value={ACTIVE_RULES_COUNT.toLocaleString()}
            icon={<TuneIcon style={{ fontSize: 18 }} />}
            iconColor={TEAL_ACCENT}
            accent={TEAL_ACCENT}
          >
            <EdrStatusPill tone="brand" label="Currently enabled" dot={false} />
          </EdrKpiCard>
        </Box>

        <EdrDataCard
          toolbar={(
            <EdrDataToolbar
              searchValue={search}
              onSearchChange={(value) => {
                setSearchInput(value);
                setPage(0);
              }}
              searchPlaceholder="Search responses…"
              filters={[
                { id: 'action', label: 'Action Type', icon: <FlashOnIcon style={{ fontSize: 14 }} /> },
                { id: 'endpoint', label: 'Endpoint', icon: <ComputerIcon style={{ fontSize: 14 }} /> },
                { id: 'org', label: 'Organization' },
                { id: 'status', label: 'Status', icon: <TrendingUpIcon style={{ fontSize: 14 }} /> },
                { id: 'severity', label: 'Severity', icon: <WarningIcon style={{ fontSize: 14 }} /> },
                { id: 'date', label: 'Date Range', icon: <CalendarTodayIcon style={{ fontSize: 14 }} /> },
              ]}
              onReset={() => {
                resetFilters();
                setPage(0);
              }}
              resetLabel="Reset Filters"
              trailing={(
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<GetAppIcon style={{ fontSize: 14 }} />}
                  style={{ textTransform: 'none' }}
                >
                  Export
                </Button>
              )}
            />
          )}
          footer={(
            <EdrTableFooter
              showing={`Showing ${pageRows.length} of ${filteredRows.length} responses`}
              hasPrevious={page > 0}
              hasNext={page + 1 < totalPages}
              onPrevious={() => setPage((current) => Math.max(0, current - 1))}
              onNext={() => setPage((current) => current + 1)}
            />
          )}
        >
          <Table
            columns={columns}
            data={pageRows as unknown as TableData[]}
            loading={false}
            showPagination={false}
            onRowClick={(row) => handleViewDetails(row as unknown as ResponseRow)}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          />
        </EdrDataCard>
      </Box>
    </>
  );
};

export default AutomatedResponse;
